import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlLocalizer } from 'tradera/lang/translate';
import {
    selectAvailableShippingCountries,
    selectShowLocalizationRegionPicker,
} from 'tradera/state/shipping-region/selectors';
import { selectGeolocation } from 'tradera/state/member/selectors';
import { dismissLocalizationRegionPicker } from 'tradera/state/shipping-region/reducer';
import {
    getAvailableShippingCountries,
    onLocalizationShippingRegionSelected,
} from 'tradera/state/shipping-region/actions';
import ApplicationErrorBoundary from 'tradera/components/application-error-boundary/application-error-boundary';
import { useUserAgent } from 'tradera/hooks/use-user-agent';
import { ShippingCountrySelectDropdown } from 'tradera/components/localization/shipping-country-select-dropdown';
import LocalizationImage from './localization-region-picker-image.svg?jsx';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { mapCountryCodeToCurrencyCode } from 'tradera/localization/map-country-code-to-currency-code';
import { Button } from '../atoms/button/button';
import { isCrawlerBot } from 'tradera/utils/is-crawler-bot';
import GdprSettings from 'tradera/utils/gdpr-settings';
import { ScrollableModal } from '../scrollable-modal/scrollable-modal';
import { HeaderWithCloseButton } from '../scrollable-modal/header-with-close-button';

export const LocalizationRegionPicker = () => {
    const showLocalizationRegionPicker =
        useSelector(selectShowLocalizationRegionPicker) &&
        GdprSettings.isFunctionalEnabled();
    const geolocation = useSelector(selectGeolocation);
    const { toLocalizedUrl } = useUrlLocalizer();
    const userAgent = useUserAgent();

    const isCrawler = isCrawlerBot(userAgent);

    const onCloseModal = () => dispatch(dismissLocalizationRegionPicker());

    const shippingCountriesTmp = useSelector(selectAvailableShippingCountries);
    const shippingCountries = useMemo(
        () =>
            shippingCountriesTmp.map(({ countryCodeIso2, countryNameEng }) => ({
                countryCodeIso2,
                name: countryNameEng,
            })),
        [shippingCountriesTmp],
    );
    const [shippingCountryCodeIso2, setShippingCountryCodeIso2] = useState(
        geolocation?.isoCode || 'se',
    );
    useEffect(() => {
        setShippingCountryCodeIso2(geolocation?.isoCode || 'se');
    }, [geolocation?.isoCode]);
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(getAvailableShippingCountries());
    }, [dispatch]);

    const isMobile = useIsMobile();

    const body = (
        <>
            <div className="text-center pt-2">
                <LocalizationImage
                    style={{ height: 112, width: 98 }}
                    className="mb-3"
                />
                <h2 className="heading-london text-styled">
                    We are happy that you found Tradera
                </h2>
                <p>
                    Choose the country where you live, to get the best
                    experience browsing items on Tradera.
                </p>
            </div>
            <ShippingCountrySelectDropdown
                shippingCountries={shippingCountries}
                selectedShippingCountryCodeIso2={shippingCountryCodeIso2}
                onSelect={(countryCodeIso2) =>
                    setShippingCountryCodeIso2(countryCodeIso2)
                }
                itemsBeforeScrollCount={isMobile ? 3 : 5}
                shippingCountryPlaceholder="Search country"
                englishCountryNames
                style={{
                    left: '1.5rem',
                    right: '1.5rem',
                    width: 'auto',
                }}
            />
        </>
    );

    return showLocalizationRegionPicker && !isCrawler ? (
        <ApplicationErrorBoundary>
            <ScrollableModal
                show
                onClose={onCloseModal}
                forceZIndex
                header={<HeaderWithCloseButton onClose={onCloseModal} />}
                footer={
                    <Button
                        theme="primary"
                        fluid
                        onClick={() => {
                            dispatch(
                                onLocalizationShippingRegionSelected({
                                    countryCodeIso2: shippingCountryCodeIso2,
                                    currencyCode: mapCountryCodeToCurrencyCode(
                                        shippingCountryCodeIso2,
                                    ),
                                    toLocalizedUrl,
                                }),
                            );
                            onCloseModal();
                        }}>
                        Done
                    </Button>
                }>
                {body}
            </ScrollableModal>
        </ApplicationErrorBoundary>
    ) : null;
};
